import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css';
import { GridType, HistoryItem, FontItem } from './types';
import { fontService } from './services/fontService';
import { BUILT_IN_FONTS } from './services/builtInFonts';
import { loadFontList, loadFontBlob, saveFont, deleteFont } from './services/fontStorage';
import {
  getHistory,
  setHistory as setStoredHistory,
  getText,
  setText as setStoredText,
  getCols,
  setCols as setStoredCols,
  getGridType,
  setGridType as setStoredGridType,
} from './services/storageService';

// Components
import Header from './components/Header';
import FontSelector from './components/FontSelector';
import TextInput from './components/TextInput';
import GridSettings from './components/GridSettings';
import Worksheet from './components/Worksheet';
import FontModal from './components/FontModal';
import AboutDialog from './components/AboutDialog';

const DEFAULT_FONT: FontItem = {
  id: 'default',
  name: '默认楷书',
  desc: '系统默认字体',
  family: 'inherit',
  isCustom: false,
};

const App: React.FC = () => {
  const [fonts, setFonts] = useState<FontItem[]>([DEFAULT_FONT]);
  const [currentFont, setCurrentFont] = useState<FontItem>(DEFAULT_FONT);
  const [fontsReady, setFontsReady] = useState(false);

  // UI state — initialize from localStorage
  const [text, setText] = useState<string>(() => getText() || '天地玄黄宇宙洪荒');
  const [cols, setCols] = useState<number>(() => getCols() || 3);
  const [gridType, setGridType] = useState<GridType>(() => getGridType() || 'mi');

  // Menu visibility
  const [showHistoryMenu, setShowHistoryMenu] = useState<boolean>(false);
  const [showColsMenu, setShowColsMenu] = useState<boolean>(false);
  const [showGridMenu, setShowGridMenu] = useState<boolean>(false);
  const [showFontModal, setShowFontModal] = useState<boolean>(false);
  const [showAbout, setShowAbout] = useState<boolean>(false);

  // Hidden file input for font upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  // History data — initialize from localStorage
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>(() => {
    const stored = getHistory();
    return stored.length > 0
      ? stored
      : [
        { text: '天地玄黄宇宙洪荒', label: '千字文' },
        { text: '赵钱孙李周吴郑王', label: '百家姓' },
        { text: '人之初性本善', label: '三字经' },
        { text: '自强不息厚德载物', label: '清华校训' },
        { text: '道可道非常道名可名非常名', label: '道德经' },
        { text: '一是我的了', label: '高频字' },
        { text: '他们有人不在时到这个上来', label: '高频字2' },
        { text: '一二三四五六七八九十', label: '中文数字' },
        { text: '杨译然', label: '杨译然' },
        ];
  });

  // Load custom fonts from IndexedDB on mount; register built-in fonts
  useEffect(() => {
    const initFonts = async () => {
      const loaded = await loadFontList();
      // Separate built-in fonts (already in IndexedDB from a previous session)
      // from truly custom user-uploaded fonts
      const customLoaded = loaded.filter(
        (f) => !BUILT_IN_FONTS.find((b) => b.id === f.id)
      );
      if (customLoaded.length > 0) {
        for (const font of customLoaded) {
          const ok = await fontService.reloadFont(font);
          if (!ok) console.warn(`Could not reload font: ${font.name}`);
        }
      }
      // Merge: built-in fonts that were previously downloaded (in IndexedDB)
      // should show as available; others show as downloadable
      const builtInWithStatus = BUILT_IN_FONTS.map((builtIn) => {
        const stored = loaded.find((f) => f.id === builtIn.id);
        if (stored) {
          // Already downloaded previously — reload it
          fontService.reloadFont(stored).catch(() => {});
          return { ...stored, needsDownload: false };
        }
        return { ...builtIn, needsDownload: true };
      });
      setFonts([DEFAULT_FONT, ...builtInWithStatus, ...customLoaded]);
      const lastId = localStorage.getItem('hzhandwriting_current_font_id');
      const allFonts = [DEFAULT_FONT, ...builtInWithStatus, ...customLoaded];
      const found = allFonts.find((f: FontItem) => f.id === lastId);
      if (found) setCurrentFont(found);
      setFontsReady(true);
    };
    initFonts();
  }, []);

  // Called on every keystroke — update text + persist, but don't touch history yet
  const handleTextChange = useCallback((newText: string) => {
    setText(newText);
    setStoredText(newText);
  }, []);

  // Called on blur — deduplicate and update history only when the user finishes editing
  const handleTextBlur = useCallback(() => {
    setText((current) => {
      const trimmed = current.trim();
      if (trimmed) {
        setHistoryItems((prev) => {
          const existing = prev.find((item) => item.text === trimmed);
          const filtered = prev.filter((item) => item.text !== trimmed);
          const label = existing ? existing.label : trimmed;
          const updated = [{ text: trimmed, label }, ...filtered].slice(0, 10);
          setStoredHistory(updated);
          return updated;
        });
      }
      return current;
    });
  }, []);

  const handleColsChange = useCallback((newCols: number) => {
    setCols(newCols);
    setStoredCols(newCols);
  }, []);

  const handleGridTypeChange = useCallback((newType: GridType) => {
    setGridType(newType);
    setStoredGridType(newType);
  }, []);

  const handleFontChange = useCallback(async (font: FontItem) => {
    // Built-in fonts need to be downloaded on first use
    if (font.isBuiltIn && font.url && !fontService.isFontLoaded(font.id)) {
      try {
        await fontService.loadFontFromUrl(font);
      } catch (err) {
        console.error('Failed to load built-in font:', err);
        alert(`字体 "${font.name}" 加载失败，请检查网络连接`);
        return;
      }
    }
    setCurrentFont(font);
    localStorage.setItem('hzhandwriting_current_font_id', font.id);
  }, []);

  const handleAddFontClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const newFont = await fontService.loadFont(file);
      await saveFont(newFont, file);
      setFonts((prev) => [...prev, newFont]);
      handleFontChange(newFont);
      alert(`字体 "${newFont.name}" 加载成功！`);
    } catch (err) {
      console.error('Failed to load font:', err);
      alert('字体加载失败，请确保文件格式正确（.ttf, .otf）');
    }

    e.target.value = '';
  };

  const handlePrintWorksheet = async () => {
    const worksheetEl = document.querySelector('[data-worksheet-grid]');
    if (!worksheetEl) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('请允许弹出窗口以进行打印');
      return;
    }

    // Read the actual computed font size from the first grid cell
    const firstCell = worksheetEl.querySelector('[data-grid-cell]') as HTMLElement | null;
    const computedFontSize = firstCell ? parseFloat(window.getComputedStyle(firstCell).fontSize) : 48;

    // For custom/built-in fonts, load the blob from IndexedDB and embed as base64
    // data URL so the print window can render the correct font.
    let fontFaceCSS = '';
    let bodyFontFamily: string;
    if (currentFont.family !== 'inherit') {
      const blob = await loadFontBlob(currentFont.id);
      if (blob) {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        fontFaceCSS = `@font-face { font-family: "${currentFont.family}"; src: url("${base64}"); }`;
        bodyFontFamily = `"${currentFont.family}", sans-serif`;
      } else {
        bodyFontFamily = 'sans-serif';
      }
    } else {
      bodyFontFamily = 'sans-serif';
    }

    // Build grid HTML from the worksheet cells
    const cells = worksheetEl.querySelectorAll('[data-grid-cell]');
    let cellsHTML = '';
    cells.forEach((cell) => {
      const el = cell as HTMLElement;
      const char = el.textContent || '';
      const gridClass = Array.from(el.classList).find((c) => c.startsWith('grid-')) || 'grid-none';
      cellsHTML += `<div class="cell ${gridClass}">${char}</div>`;
    });

    printWindow.document.write(`<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8">
  <title>字帖 - ${text}</title>
  <style>
    ${fontFaceCSS}
    @page { size: A4 portrait; margin: 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: ${bodyFontFamily}; padding: 10px; }
    .worksheet-header { text-align: center; margin-bottom: 16px; font-size: 14px; color: #666; }
    .grid { display: grid; gap: 2px; grid-template-columns: repeat(${cols}, 1fr); }
    .cell {
      aspect-ratio: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
      font-size: ${computedFontSize}px;
      line-height: 1;
      overflow: hidden;
    }
    .grid-none { background: white; }
    .grid-square { background: white; border: 1px solid #FCA5A5; }
    .grid-tian {
      background:
        linear-gradient(to bottom, #FCA5A5 1px, transparent 1px) center top / 100% 50% no-repeat,
        linear-gradient(to right, #FCA5A5 1px, transparent 1px) left center / 50% 100% no-repeat;
      border: 1px solid #FCA5A5;
    }
    .grid-mi {
      background:
        linear-gradient(to bottom, #FCA5A5 1px, transparent 1px) center top / 100% 50% no-repeat,
        linear-gradient(to right, #FCA5A5 1px, transparent 1px) left center / 50% 100% no-repeat,
        linear-gradient(45deg, #FCA5A5 1px, transparent 1px) top left / 50% 50% no-repeat,
        linear-gradient(-45deg, #FCA5A5 1px, transparent 1px) top right / 50% 50% no-repeat;
      border: 1px solid #FCA5A5;
    }
    @media print {
      body { padding: 0; }
      .worksheet-header { margin-bottom: 10px; }
    }
  </style>
</head>
<body>
  <div class="worksheet-header">${text} · ${cols}字/行 · ${currentFont.name}</div>
  <div class="grid">${cellsHTML}</div>
</body>
</html>`);

    printWindow.document.close();

    printWindow.document.fonts.ready.then(() => {
      printWindow.print();
    });
  };

  const handleClearFonts = async () => {
    if (!confirm('确定要清除所有已加载的自定义字体吗？')) return;

    // Delete all custom fonts from IndexedDB
    for (const font of fonts) {
      if (font.isCustom) {
        await deleteFont(font.id);
      }
    }

    // Reset to default font, keep built-in fonts in the list
    setFonts([DEFAULT_FONT, ...BUILT_IN_FONTS]);
    setCurrentFont(DEFAULT_FONT);
    localStorage.removeItem('hzhandwriting_current_font_id');

    alert('已清除所有自定义字体缓存');
  };

  if (!fontsReady) {
    return null;
  }

  return (
    <div className="min-h-screen bg-paper flex justify-center items-start sm:py-8">
      <div className="w-full max-w-[375px] min-h-[812px] bg-white shadow-2xl overflow-hidden relative sm:rounded-[3rem] sm:border-8 sm:border-gray-800 flex flex-col">
        <div className="bg-gray-800 text-white text-[10px] px-6 py-1 justify-between sm:flex hidden">
          <span>9:41</span>
          <div className="flex gap-1">
            <i className="fa fa-signal"></i>
            <i className="fa fa-wifi"></i>
            <i className="fa fa-battery-full"></i>
          </div>
        </div>

        <Header
          onAddFont={handleAddFontClick}
          onClearFonts={handleClearFonts}
          onShowAbout={() => setShowAbout(true)}
          onPrintWorksheet={handlePrintWorksheet}
          customFontCount={fonts.filter((f) => f.isCustom).length}
        />

        <main className="flex-1 overflow-y-auto scroll-hide p-3 bg-paper">
          <FontSelector
            currentFont={currentFont}
            onOpenModal={() => setShowFontModal(true)}
          />
          <TextInput
            text={text}
            onTextChange={handleTextChange}
            onTextBlur={handleTextBlur}
            historyItems={historyItems}
            showHistoryMenu={showHistoryMenu}
            setShowHistoryMenu={setShowHistoryMenu}
            currentLabel={historyItems.find((item) => item.text === text)?.label ?? ''}
          />

          <GridSettings
            cols={cols}
            setCols={handleColsChange}
            gridType={gridType}
            setGridType={handleGridTypeChange}
            showColsMenu={showColsMenu}
            setShowColsMenu={setShowColsMenu}
            showGridMenu={showGridMenu}
            setShowGridMenu={setShowGridMenu}
          />

          <Worksheet
            text={text}
            cols={cols}
            gridType={gridType}
            currentFont={currentFont}
          />
        </main>

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".ttf,.otf"
          onChange={handleFileChange}
        />

        {showFontModal && (
          <FontModal
            fonts={fonts}
            currentFont={currentFont}
            onSelectFont={(font) => {
              handleFontChange(font);
              setShowFontModal(false);
            }}
            onClose={() => setShowFontModal(false)}
          />
        )}

        {showAbout && (
          <AboutDialog onClose={() => setShowAbout(false)} />
        )}
      </div>
    </div>
  );
};

export default App;
